/**
 * Was der Mailverlauf eines Chats für Kunde und Termin hergibt — gebraucht von
 * „Besichtigung planen" und „Als Kunde übernehmen" (Benjamin, 03./04.10.2026).
 * Die Logik steht in lib/terminAusMail.ts und lib/anrede.ts; hier nur die
 * Verdrahtung mit dem Chat und der eigenen Signatur.
 */
import { useMemo } from "react";

import { anredeAusGruss, namenTeilen, type Anrede } from "../lib/anrede";
import { ausVerlauf, terminVorschlaege, type VerlaufFund } from "../lib/terminAusMail";
import type { Faden } from "./useNachrichten";
import { useMailSignatur } from "./usePost";

function mails(f: Faden) {
  return f.nachrichten.map((n) => ({ richtung: n.richtung, text: n.voll || n.text, zeitpunkt: n.zeitpunkt }));
}

/** Adresse, Telefon (ohne eigene Nummern aus der Signatur) und der erste Terminfund. */
export function useVerlaufFund(f: Faden) {
  const signatur = useMailSignatur().data?.signatur || "";
  return useMemo(() => {
    const eigene = signatur.match(/(?:\+49|0)[\d /()-]{7,18}\d/g) || [];
    return ausVerlauf(mails(f), eigene);
  }, [f, signatur]);
}

/** Terminvorschläge aus den letzten zehn Nachrichten, der zuletzt genannte zuerst. */
export function useTerminVorschlaege(f: Faden) {
  return useMemo(() => terminVorschlaege(mails(f), 10), [f]);
}

/** Ein Absendername, mit dem man nichts anfangen kann: „P", „info", eine Mailadresse. */
function schwacherName(name: string): boolean {
  const n = (name || "").trim();
  return n.replace(/[^A-Za-zÄÖÜäöüß]/g, "").length < 3 || n.includes("@") || /^(info|kontakt|mail|office)$/i.test(n);
}

/**
 * Anrede, Vor- und Nachname für einen neuen Privatkunden: der Name aus dem
 * Absender, die Anrede aus unserer eigenen Begrüßung im Verlauf („Guten Tag,
 * Herr Witting" → Herr). Taugt der Absendername nichts („P"), gilt der Name aus
 * dem Mailtext (`fund.name`: „Fam. Fellendorf", Zeile unter dem Gruß — 13.10.2026).
 * Nur ein Vorschlag — das Feld fragt danach.
 */
export function neuerKundeName(f: Faden, name: string, fund?: VerlaufFund): { anrede: Anrede; vorname: string; nachname: string } {
  const eigene = f.nachrichten.filter((n) => n.richtung === "out").map((n) => n.voll || n.text);
  if (schwacherName(name) && fund?.name) {
    const { anrede, vorname, nachname } = fund.name;
    return { anrede: anrede || anredeAusGruss(eigene, nachname), vorname, nachname };
  }
  const { vorname, nachname } = namenTeilen(name);
  return { anrede: anredeAusGruss(eigene, nachname), vorname, nachname };
}
